import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import type { Assignment, Person, Project } from "../services/api";

export interface AssignmentFormValues {
  assignment_id?: string;
  project_id: string;
  person_email: string;
  status: string;
  start_date?: string;
  end_date?: string;
  allocation?: string;
  notes?: string;
}

interface AssignmentFormProps {
  initial?: AssignmentFormValues;
  projects: Project[];
  people: Person[];
  onSubmit: (values: AssignmentFormValues) => void;
  onCancel: () => void;
  isSaving?: boolean;
}

export const ASSIGNMENT_STATUSES = [
  "SELF_NOMINATED",
  "NOMINATED",
  "SUBMITTED_TO_AWS",
  "INTERVIEW",
  "ACCEPTED",
  "DECLINED",
];

export function AssignmentForm({ initial, projects, people, onSubmit, onCancel, isSaving }: AssignmentFormProps) {
  const [form, setForm] = useState<AssignmentFormValues>(createInitialState(initial));

  useEffect(() => {
    setForm(createInitialState(initial));
  }, [initial]);

  function createInitialState(seed?: AssignmentFormValues): AssignmentFormValues {
    return {
      assignment_id: seed?.assignment_id,
      project_id: seed?.project_id ?? "",
      person_email: seed?.person_email ?? "",
      status: seed?.status ?? "NOMINATED",
      start_date: seed?.start_date ?? "",
      end_date: seed?.end_date ?? "",
      allocation: seed?.allocation ?? "",
      notes: seed?.notes ?? "",
    };
  }

  function handleChange<K extends keyof AssignmentFormValues>(field: K, value: AssignmentFormValues[K]) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    onSubmit(form);
  }

  return (
    <form className="form-grid" onSubmit={handleSubmit}>
      <label className="span-2">
        Project
        <select value={form.project_id} required onChange={(event) => handleChange("project_id", event.target.value)}>
          <option value="">Select project</option>
          {projects.map((project) => (
            <option key={project.project_id} value={project.project_id}>
              {project.title} ({project.project_id})
            </option>
          ))}
        </select>
      </label>
      <label className="span-2">
        Person
        <select value={form.person_email} required onChange={(event) => handleChange("person_email", event.target.value)}>
          <option value="">Select consultant</option>
          {people.map((person) => (
            <option key={person.email} value={person.email}>
              {person.name} ({person.email})
            </option>
          ))}
        </select>
      </label>
      <label>
        Status
        <select value={form.status} required onChange={(event) => handleChange("status", event.target.value)}>
          {ASSIGNMENT_STATUSES.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </label>
      <label>
        Allocation
        <input
          type="text"
          placeholder="e.g., 100% or 0.5 FTE"
          value={form.allocation ?? ""}
          onChange={(event) => handleChange("allocation", event.target.value)}
        />
      </label>
      <label>
        Start Date
        <input type="date" value={form.start_date ?? ""} onChange={(event) => handleChange("start_date", event.target.value)} />
      </label>
      <label>
        End Date
        <input type="date" value={form.end_date ?? ""} onChange={(event) => handleChange("end_date", event.target.value)} />
      </label>
      <label className="span-2">
        Notes
        <textarea value={form.notes ?? ""} rows={3} onChange={(event) => handleChange("notes", event.target.value)} />
      </label>
      <div className="form-actions span-2">
        <button type="submit" className="primary" disabled={isSaving}>
          {isSaving ? "Saving…" : form.assignment_id ? "Update Assignment" : "Create Assignment"}
        </button>
        <button type="button" className="ghost" onClick={onCancel} disabled={isSaving}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export function toFormValues(assignment: Assignment): AssignmentFormValues {
  return {
    assignment_id: assignment.assignment_id,
    project_id: assignment.project_id,
    person_email: assignment.person_email,
    status: assignment.status,
    start_date: assignment.start_date,
    end_date: assignment.end_date,
    allocation: assignment.allocation,
    notes: assignment.notes,
  };
}
